<template>
  <div class="welcome-root">

    <!-- ─── ANIMATED BG ─── -->
    <div class="animated-bg">
      <div class="gradient-orb orb-1"></div>
      <div class="gradient-orb orb-2"></div>
      <div class="gradient-orb orb-3"></div>
    </div>

    <!-- ─── LOGO BAR ─── -->
    <div class="logo-bar">
      <div class="logo-bar-inner">
        <img :src="logoSrc" alt="ERA ERP" class="top-logo" @error="logoError = true" v-if="!logoError" />
        <div class="logo-fallback-top" v-else>
          <span class="era-text-top">ERA</span>
        </div>
        <div class="logo-bar-tagline">Enterprise Resource Application</div>
      </div>
    </div>

    <!-- ─── PANELS ─── -->
    <div class="panels-row">

      <!-- ─── LEFT PANEL ─── -->
      <div class="left-panel">
        <div class="left-content">

          <h1 class="welcome-title">
            <span class="gradient-text">Bine ați venit</span>
          </h1>
          <p class="welcome-subtitle">Sistem ERP Enterprise de Ultimă Generație</p>

          <div class="features-grid">
            <div class="feature-card" v-for="(f, i) in features" :key="i">
              <div class="feature-icon-wrapper">
                <v-icon :color="f.color" size="26">{{ f.icon }}</v-icon>
              </div>
              <div class="feature-content">
                <h3 class="feature-title">{{ f.title }}</h3>
                <p class="feature-desc">{{ f.desc }}</p>
              </div>
            </div>
          </div>

          <!-- <div class="stats-row">
            <div class="stat-item">
              <div class="stat-value">99.9%</div>
              <div class="stat-label">Uptime</div>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <div class="stat-value">24/7</div>
              <div class="stat-label">Suport</div>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <div class="stat-value">ISO</div>
              <div class="stat-label">Certificat</div>
            </div>
          </div> -->

        </div>
      </div>

      <!-- ─── RIGHT PANEL ─── -->
      <div class="right-panel">
        <div class="greeting-card">

          <div class="header-badge">
            <v-icon size="18" color="#ff5722">mdi-check-circle</v-icon>
            <span>Autentificat cu succes</span>
          </div>

          <p class="greeting-label">{{ greetingIcon }}&nbsp;&nbsp;{{ greetingText }}</p>
          <h2 class="greeting-name">{{ username }}</h2>

          <div class="meta-wrap">
            <span class="meta-chip">
              <i class="mdi mdi-calendar-today"></i>
              {{ formattedDate }}
            </span>
            <span class="meta-chip" v-if="companyName">
              <i class="mdi mdi-office-building-outline"></i>
              {{ companyName }}
            </span>
          </div>

          <div class="card-divider"></div>

          <div class="footer-links">
            <span class="footer-link">
              <v-icon size="15">mdi-lifebuoy</v-icon>
              Centru Ajutor
            </span>
            <span class="link-sep">•</span>
            <span class="footer-link">
              <v-icon size="15">mdi-shield-check</v-icon>
              Securitate
            </span>
            <span class="link-sep">•</span>
            <span class="footer-link">
              <v-icon size="15">mdi-file-document-outline</v-icon>
              Termeni
            </span>
          </div>

        </div>

        <p class="copyright-text">© {{ currentYear }} Else Digital Solutions S.R.L.</p>
      </div>

    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import JwtServices from '@/services/JwtServices';

export default defineComponent({
  name: 'WelcomeView',
  data() {
    return {
      logoError: false,
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      logoSrc: require('@/assets/Logo Mida Soft Business.svg'),
      features: [
        { icon: 'mdi-shield-check', color: '#fff', title: 'Securitate Avansată', desc: 'Criptare end-to-end' },
        { icon: 'mdi-chart-line', color: '#fff', title: 'Analytics Real-Time', desc: 'Rapoarte instantanee' },
        { icon: 'mdi-cloud-sync', color: '#fff', title: 'Cloud Sync', desc: 'Sincronizare automată' },
        { icon: 'mdi-lightning-bolt', color: '#fff', title: 'Performanță', desc: 'Viteză maximă' },
      ],
    };
  },
  computed: {
    hour(): number {
      return new Date().getHours();
    },
    greetingText(): string {
      if (this.hour >= 5 && this.hour < 12) return 'Bună dimineața';
      if (this.hour >= 12 && this.hour < 18) return 'Bună ziua';
      return 'Bună seara';
    },
    greetingIcon(): string {
      if (this.hour >= 5 && this.hour < 12) return '☀️';
      if (this.hour >= 12 && this.hour < 18) return '🌤️';
      return '🌙';
    },
    username(): string {
      const raw = JwtServices.GetUsername() || 'Utilizator';
      return raw
        .split(/[.\s_-]+/)
        .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');
    },
    companyName(): string {
      try {
        const raw = localStorage.getItem('ERA_Company');
        if (!raw) return '';
        const obj = JSON.parse(raw);
        return obj?.Name || obj?.Nume || '';
      } catch {
        return '';
      }
    },
    formattedDate(): string {
      return new Date().toLocaleDateString('ro-RO', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
      });
    },
    currentYear(): number {
      return new Date().getFullYear();
    },
  },
});
</script>

<style scoped>
/* ─── ROOT ─── */
.welcome-root {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  position: relative;
  overflow: hidden;
  background: #f5f7fa;
}

/* ─── ANIMATED ORBS ─── */
.animated-bg {
  position: absolute;
  inset: 0;
  overflow: hidden;
  z-index: 0;
  pointer-events: none;
}

.gradient-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.25;
  animation: float 20s infinite ease-in-out;
}

.orb-1 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #ff5722, #f4511e);
  top: -10%;
  left: -10%;
  animation-delay: 0s;
}

.orb-2 {
  width: 350px;
  height: 350px;
  background: linear-gradient(135deg, #ff6f00, #ff5722);
  bottom: -10%;
  right: -5%;
  animation-delay: 7s;
}

.orb-3 {
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, #ff7043, #ff6f00);
  top: 50%;
  left: 50%;
  animation-delay: 14s;
}

@keyframes float {

  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }

  33% {
    transform: translate(30px, -50px) scale(1.1);
  }

  66% {
    transform: translate(-20px, 20px) scale(0.9);
  }
}

/* ─── LOGO BAR ─── */


.logo-bar-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 14px 2rem;
}

.top-logo {
  max-height: 74px;
  max-width: 200px;
  object-fit: contain;
}

.logo-fallback-top {
  display: flex;
  align-items: center;
}

.era-text-top {
  font-size: 1.8rem;
  font-weight: 900;
  letter-spacing: 0.3rem;
  background: linear-gradient(135deg, #f4511e, #ff6f00);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.logo-bar-tagline {
  font-size: 0.7rem;
  font-weight: 500;
  color: #a0aec0;
  text-transform: uppercase;
  letter-spacing: 0.2rem;
  border-left: 1px solid #e2e8f0;
  padding-left: 1rem;
}

/* ─── PANELS ROW ─── */
.panels-row {
  flex: 1;
  display: flex;
  position: relative;
  z-index: 1;
  overflow: hidden;
  border-radius: 16px;
}

/* ─── LEFT PANEL ─── */
.left-panel {
  flex: 1;
  background: linear-gradient(135deg, #f4511e 0%, #ff5722 50%, #ff6f00 100%);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  overflow: hidden;
  border-radius: 16px;
}

.left-panel::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
}

.left-panel::after {
  content: '';
  position: absolute;
  inset: 0;
  background: repeating-linear-gradient(45deg, transparent, transparent 10px,
      rgba(255, 255, 255, 0.02) 10px, rgba(255, 255, 255, 0.02) 20px);
}

.left-content {
  position: relative;
  z-index: 1;
  color: white;
  max-width: 480px;
  width: 100%;
}

.welcome-title {
  font-size: 2.5rem;
  font-weight: 800;
  margin: 0 0 0.75rem;
  animation: fadeInUp 0.8s ease-out 0.1s both;
}

.gradient-text {
  background: linear-gradient(135deg, #fff 0%, #ffe0d5 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.welcome-subtitle {
  font-size: 1rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 2rem;
  animation: fadeInUp 0.8s ease-out 0.2s both;
}

/* ─── FEATURES ─── */
.features-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;
  animation: fadeInUp 0.8s ease-out 0.3s both;
}

.feature-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 1.25rem;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: default;
}

.feature-card:hover {
  background: rgba(255, 255, 255, 0.18);
  transform: translateY(-4px);
  box-shadow: 0 16px 32px rgba(0, 0, 0, 0.12);
}

.feature-icon-wrapper {
  width: 40px;
  height: 40px;
  min-width: 40px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
}

.feature-card:hover .feature-icon-wrapper {
  transform: scale(1.1) rotate(5deg);
}

.feature-content {
  flex: 1;
}

.feature-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: white;
  margin: 0 0 0.15rem;
}

.feature-desc {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.75);
  margin: 0;
}

/* ─── STATS ─── */
.stats-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  padding: 1.25rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  animation: fadeInUp 0.8s ease-out 0.4s both;
}

.stat-item {
  text-align: center;
}

.stat-value {
  font-size: 1.6rem;
  font-weight: 700;
  color: white;
  margin-bottom: 2px;
}

.stat-label {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 1px;
}

.stat-divider {
  width: 1px;
  height: 36px;
  background: rgba(255, 255, 255, 0.25);
}

/* ─── RIGHT PANEL ─── */
.right-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 2rem;
  position: relative;
  z-index: 1;
  gap: 1.5rem;
}

.greeting-card {
  background: white;
  border-radius: 24px;
  padding: 2.5rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 420px;
  animation: fadeInRight 0.8s ease-out;
}

.header-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 1rem;
  background: linear-gradient(135deg, #ffe0d5, #ffcdd2);
  border-radius: 50px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #ff5722;
  margin-bottom: 1.75rem;
}

.greeting-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: #9e9e9e;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin: 0 0 0.5rem;
}

.greeting-name {
  font-size: 2rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 1.5rem;
  background: linear-gradient(135deg, #f4511e, #ff6f00);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.meta-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 1.75rem;
}

.meta-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff3e0;
  color: #e64a19;
  border: 1px solid #ffccbc;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
}

.meta-chip .mdi {
  font-size: 14px;
}

.card-divider {
  height: 1px;
  background: #e2e8f0;
  margin-bottom: 1.5rem;
}

.footer-links {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.footer-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8rem;
  color: #718096;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s;
}

.footer-link:hover {
  color: #ff5722;
}

.link-sep {
  color: #cbd5e0;
  font-size: 0.8rem;
}

.copyright-text {
  font-size: 0.8rem;
  color: #a0aec0;
  margin: 0;
  text-align: center;
}

/* ─── ANIMATIONS ─── */
@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-16px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(24px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInRight {
  from {
    opacity: 0;
    transform: translateX(24px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* ─── RESPONSIVE ─── */
@media (max-width: 960px) {
  .panels-row {
    flex-direction: column;
  }

  .left-panel {
    padding: 2.5rem 2rem;
  }

  .welcome-title {
    font-size: 2rem;
  }
}

@media (max-width: 600px) {
  .features-grid {
    grid-template-columns: 1fr;
  }

  .left-panel {
    padding: 2rem 1.5rem;
  }

  .greeting-card {
    padding: 1.75rem;
  }

  .greeting-name {
    font-size: 1.6rem;
  }

  .logo-bar-tagline {
    display: none;
  }
}
</style>
